import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Keyboard, Platform, TextInput as RnTextInput, TextStyle, View } from 'react-native';

import { IconClose, Pressable, Text, createMqStyles, tokens, useMqSelect } from '@fhs/ui';
import { useBoxRef, useDebouncedCallback } from '@fhs/utils';
import useLocationPredictions from '@fhs-legacy/frontend/src/hooks/geolocation/use-location-predictions';

import { PredictionsDropdown } from './predictions-dropdown';
import { AddressSearchInputHandle, AddressSearchInputProps, LocationPrediction } from './types';

/** Google only returns useful predictions once the query is specific enough. */
const MIN_QUERY_LENGTH = 3;
const PREDICTIONS_DEBOUNCE_MS = 300;
const MAX_PREDICTIONS_MOBILE = 3;
const MAX_PREDICTIONS_DESKTOP = 5;

const isWeb = Platform.OS === 'web';

/**
 * Address text input backed by Google Places predictions, shared by the pickup search and the
 * delivery address field. It owns the typing, the suggestions and the clear button; what a picked
 * prediction means is left to the caller.
 */
export const AddressSearchInput = forwardRef<AddressSearchInputHandle, AddressSearchInputProps>(
  function AddressSearchInput(
    {
      value,
      placeholder,
      addressesOnly = false,
      helperText,
      hasError = false,
      disabled = false,
      autoFocus,
      returnKeyType = 'search',
      addressHeadingNativeId,
      testID,
      clearTestID,
      helperTestID,
      getPredictionTestID,
      clearActionName,
      predictionActionName,
      style,
      inputContainerStyle,
      inputStyle,
      dropdownStyle,
      onFocus,
      onBlur,
      onChangeText,
      onSelectPrediction,
      onClear,
      onTabOut,
    },
    forwardedRef
  ) {
    const styles = useAddressSearchInputMqStyles();
    const helperId = `address-search-input-helper-${useId().replace(/:/g, '')}`;

    const inputRef = useRef<RnTextInput | null>(null);
    const [text, setText] = useState(value ?? '');
    // `onBlur` can fire with a stale closure — a press inside the field blurs the input before
    // React has re-rendered with the text that press produced. It also lets the `value` sync
    // compare against the field without re-running on every keystroke.
    const textRef = useRef(text);
    const [isFocused, setIsFocused] = useState(false);
    // A picked prediction fills the field with its own description, which would otherwise
    // immediately re-open the list with that exact address as the only match.
    const [hasPickedPrediction, setHasPickedPrediction] = useState(Boolean(value));
    // Deliberately not seeded from `value`: mounting with a known address would spend a
    // Places request re-predicting an address the guest already chose.
    const [query, setQuery] = useState('');
    // A pick blurs the input before the picked text reaches it, and the native input can report
    // one last change of its own as editing ends, still holding what the guest had typed: iOS
    // flushes a pending edit, and some Android keyboards commit a correction to the word they were
    // composing. Taken as typing, that would undo the pick and reopen the list. The guest cannot
    // type again until the field is refocused, so nothing in between is an edit.
    const isSettlingPickRef = useRef(false);

    // Every keystroke would otherwise cost a Places request.
    const debounceQuery = useDebouncedCallback(setQuery, PREDICTIONS_DEBOUNCE_MS);
    const cancelPredictionsRef = useRef<(() => void) | undefined>();
    const { isLoading: isLoadingPredictions, predictions: locationPredictions } =
      useLocationPredictions(query, { addressesOnly });
    const maxPredictions = useMqSelect(
      { $gteDesktop: MAX_PREDICTIONS_DESKTOP },
      MAX_PREDICTIONS_MOBILE
    );
    const predictions = locationPredictions.slice(0, maxPredictions);

    const onTabOutRef = useBoxRef(onTabOut);
    const handlesTabOut = Boolean(onTabOut);

    const setFieldText = useCallback((next: string) => {
      textRef.current = next;
      setText(next);
    }, []);

    const resetField = useCallback(() => {
      cancelPredictionsRef.current?.();
      setFieldText('');
      setQuery('');
      setHasPickedPrediction(false);
      onClear?.();
    }, [onClear, setFieldText]);

    const clearField = useCallback(() => {
      resetField();
      setIsFocused(false);
      inputRef.current?.blur();
      onBlur?.('');
    }, [onBlur, resetField]);

    useImperativeHandle(
      forwardedRef,
      () => ({
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
        clear: clearField,
      }),
      [clearField]
    );

    useEffect(() => {
      if (!value || value === textRef.current) {
        return;
      }

      cancelPredictionsRef.current?.();
      setFieldText(value);
      setQuery('');
      setHasPickedPrediction(true);
    }, [setFieldText, value]);

    const handleChangeText = useCallback(
      (next: string) => {
        if (isSettlingPickRef.current) {
          return;
        }

        setFieldText(next);
        setHasPickedPrediction(false);
        cancelPredictionsRef.current = debounceQuery(next);
        onChangeText?.(next);
      },
      [debounceQuery, onChangeText, setFieldText]
    );

    const handleSelectPrediction = useCallback(
      (prediction: LocationPrediction) => {
        const searchText = textRef.current;

        cancelPredictionsRef.current?.();
        setFieldText(prediction.description);
        setQuery('');
        setHasPickedPrediction(true);
        isSettlingPickRef.current = true;
        // The results the guest just asked for are behind the keyboard.
        Keyboard.dismiss();
        // Web has no keyboard to dismiss, so the field would otherwise stay active.
        inputRef.current?.blur();
        onSelectPrediction(prediction, searchText);
      },
      [onSelectPrediction, setFieldText]
    );

    const onClearPress = useCallback(() => {
      resetField();
      // The button goes away with the text, so focus would otherwise drop to the page.
      inputRef.current?.focus();
    }, [resetField]);

    const handleFocus = useCallback(() => {
      isSettlingPickRef.current = false;
      setIsFocused(true);
      onFocus?.();
    }, [onFocus]);

    const handleBlur = useCallback(() => {
      setIsFocused(false);
      onBlur?.(textRef.current);
    }, [onBlur]);

    // React Native Web does not surface Tab through `onKeyPress`, so the listener has to go on
    // the DOM node.
    useEffect(() => {
      if (!isWeb || !handlesTabOut) {
        return;
      }

      const node = inputRef.current as unknown as HTMLInputElement | null;
      if (!node) {
        return;
      }

      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Tab' || event.shiftKey || !hasPickedPrediction) {
          return;
        }

        if (onTabOutRef.current?.()) {
          event.preventDefault();
        }
      };

      node.addEventListener('keydown', onKeyDown);

      return () => node.removeEventListener('keydown', onKeyDown);
    }, [handlesTabOut, hasPickedPrediction, onTabOutRef]);

    const hasHelperText = Boolean(helperText);
    // Keep the list up after the input blurs. On web the field loses focus on mousedown of a
    // suggestion, and unmounting here would drop the click. Checking the live text as well as the
    // debounced query closes it as soon as the guest deletes below the minimum.
    const showPredictions =
      !disabled &&
      !hasPickedPrediction &&
      text.trim().length >= MIN_QUERY_LENGTH &&
      query.trim().length >= MIN_QUERY_LENGTH;
    const labelledByNative = addressHeadingNativeId && !isWeb;

    return (
      <View style={[styles.container, style]}>
        <View style={[styles.inputAnchor, hasHelperText && styles.inputAnchorAboveHelper]}>
          <View
            style={[
              styles.inputContainer,
              isFocused && styles.inputContainerFocused,
              hasError && styles.inputContainerError,
              inputContainerStyle,
            ]}
          >
            <RnTextInput
              ref={inputRef}
              testID={testID}
              value={text}
              editable={!disabled}
              autoFocus={autoFocus}
              onChangeText={handleChangeText}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={placeholder}
              placeholderTextColor={tokens.colors.$placeholderText}
              // The browser's own address autofill would open on top of the predictions.
              autoComplete="off"
              autoCorrect={false}
              returnKeyType={returnKeyType}
              underlineColorAndroid="transparent"
              textAlignVertical="center"
              // Only a picked prediction does anything, so submitting just puts the keyboard away.
              onSubmitEditing={() => inputRef.current?.blur()}
              style={[styles.input, inputStyle]}
              accessibilityLabel={addressHeadingNativeId ? undefined : placeholder}
              accessibilityHint={isWeb ? undefined : helperText}
              aria-disabled={disabled}
              {...(labelledByNative ? { accessibilityLabelledBy: addressHeadingNativeId } : {})}
              {...(addressHeadingNativeId && isWeb
                ? ({ 'aria-labelledby': addressHeadingNativeId } as object)
                : {})}
              {...(hasHelperText && isWeb
                ? ({ 'aria-describedby': helperId, 'aria-invalid': hasError } as object)
                : {})}
            />

            {Boolean(text) && (
              <Pressable
                testID={clearTestID}
                accessibilityLabel={`Clear Text. ${text}`}
                dd-action-name={clearActionName}
                onPress={onClearPress}
                disabled={disabled}
                borderRadius={999}
                hitSlop={12}
                style={styles.clearButton}
              >
                <IconClose
                  size={20}
                  color={disabled ? tokens.colors.$blackOpacity30 : tokens.colors.$blackOpacity75}
                />
              </Pressable>
            )}
          </View>

          {showPredictions && (
            <PredictionsDropdown
              predictions={predictions}
              isLoading={isLoadingPredictions}
              onSelectPrediction={handleSelectPrediction}
              getPredictionTestID={getPredictionTestID}
              predictionActionName={predictionActionName}
              style={dropdownStyle}
            />
          )}
        </View>

        {hasHelperText && (
          <Text.Paragraph
            size="xs"
            nativeID={helperId}
            testID={helperTestID}
            style={[styles.helper, hasError && styles.helperError]}
            // Native screen readers get this as the input's hint instead, so it is not read twice.
            {...(isWeb
              ? ({ 'aria-live': 'polite' } as object)
              : ({
                  accessibilityElementsHidden: true,
                  importantForAccessibility: 'no-hide-descendants',
                } as const))}
          >
            {helperText}
          </Text.Paragraph>
        )}
      </View>
    );
  }
);

const useAddressSearchInputMqStyles = createMqStyles({
  container: {
    $base: {
      overflow: 'visible',
      position: 'relative',
    },
  },
  // The predictions hang from the input box rather than the whole field, covering the helper text.
  inputAnchor: {
    $base: {
      overflow: 'visible',
      position: 'relative',
    },
  },
  // Only when there is helper text to cover: on web a stacking level of its own also caps how high
  // the predictions can stack against the rest of the page.
  inputAnchorAboveHelper: {
    $base: {
      zIndex: 1,
    },
  },
  // Figma: white fill, 1pt Black 30% stroke, 4pt radius, 48pt tall, 16pt inset.
  inputContainer: {
    $base: {
      alignItems: 'center',
      backgroundColor: tokens.colors.$white,
      borderColor: tokens.colors.$blackOpacity30,
      borderRadius: 4,
      borderWidth: 1,
      flexDirection: 'row',
      height: 48,
      paddingHorizontal: 16,
    },
  },
  inputContainerFocused: {
    $base: {
      borderColor: tokens.colors.$houseDark,
    },
  },
  inputContainerError: {
    $base: {
      borderColor: tokens.colors.$error,
    },
  },
  input: {
    $base: {
      color: tokens.colors.$houseDark,
      flex: 1,
      fontFamily: 'Montserrat',
      fontSize: 14,
      ...Platform.select({
        android: {
          // lineHeight plus the default EditText padding pushes the glyphs
          // outside the clip bounds, so the field looks empty while typing.
          paddingVertical: 0,
        },
        default: { lineHeight: 20 },
        // Figma truncates a long address with an ellipsis; only browser inputs support it.
        web: { lineHeight: 20, outlineWidth: 0, textOverflow: 'ellipsis' } as TextStyle,
      }),
    },
  },
  clearButton: {
    $base: {
      alignItems: 'center',
      height: 24,
      justifyContent: 'center',
      width: 24,
    },
  },
  // Figma: 10/14 below desktop and 12/17 at desktop, 8pt under the input.
  helper: {
    $base: {
      lineHeight: 14,
      marginTop: 8,
    },
    $gteDesktop: {
      lineHeight: 17,
    },
  },
  helperError: {
    $base: {
      color: tokens.colors.$error,
    },
  },
});
